import React, { useState, useEffect } from 'react';
import { Card, Stack, Button, Drawer } from '@mui/material';
import { ACTIONS } from '../../config/const.config';
import {
  LISTING_COLUMNS,
  LISTING_ACTIONS,
} from '../../config/module-configs/app-sessions.config';
import ListData from '../table-elements/list-data.component';
import Iconify from '../common/iconify.component';
import ViewSession from './view-session.component';
import { getDataList } from '../../services/reports.service';
import FilterSessions from './filter-sessions.component';
import GameVideoSession from './game-video-session.component';
import RelatedAppItems from './related-app-items-list.component';
import { formatDateToUtc } from '../../utils/datetime.util';

const ListSessions = () => {
  const columns = LISTING_COLUMNS;
  const actions = LISTING_ACTIONS;

  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 20,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    appliedFilters: {
      search: '',
      filterApp: null,
      platforms: [],
      brd: null,
      brdsdkversion: null,
      sessionStartDate: null,
      sessionEndDate: null,
    },
    filtersCounter: 0,
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [openFilters, setOpenFilters] = useState(false);

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const getDialogTitle = (data) => {
    if (data.appType === 'ottapp') {
      return `Video Session Details - ${data.deviceId}`;
    }
    return `Game Session Details - ${data.deviceId}`;
  };
  const handleActionSelect = (selectedAction, selectedData) => {
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };
  const handleClick = (column, row) => {
    if (
      column.dataKey === ACTIONS.GAME_VIDEO_SESSION.value ||
      column.dataKey === ACTIONS.RELATED_APP_ITEM_CLICKED.value
    ) {
      handleActionSelect(column.dataKey, row);
    }
  };
  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };

  const handleToggleFilters = () => {
    setOpenFilters(!openFilters);
  };
  const handleApplyFilters = (selectedFilters) => {
    let counter = 0;
    if (selectedFilters.search.trim()) counter += 1;
    if (selectedFilters.filterApp) counter += 1;
    if (selectedFilters.platforms.length > 0) counter += 1;
    if (selectedFilters.brd !== null) counter += 1;
    if (selectedFilters.brdsdkversion !== null) counter += 1;
    if (selectedFilters.sessionStartDate) counter += 1;
    if (selectedFilters.sessionEndDate) counter += 1;

    setOptions({
      ...options,
      appliedFilters: {
        ...selectedFilters,
      },
      filtersCounter: counter,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
    handleToggleFilters();
  };
  const handleClearFilters = () => {
    handleToggleFilters();
    setOptions({
      ...options,
      appliedFilters: {
        search: '',
        filterApp: null,
        platforms: [],
        brd: null,
        brdsdkversion: null,
        sessionStartDate: null,
        sessionEndDate: null,
      },
      filtersCounter: 0,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  useEffect(() => {
    const params = [];
    params.push(`page=${options.page + 1}`);
    params.push(`perPage=${options.rowsPerPage}`);
    if (options.sortBy && options.sortOrder) {
      params.push(`sortBy=${options.sortBy}`);
      params.push(`sortOrder=${options.sortOrder}`);
    }
    const filters = options.appliedFilters;
    if (filters.search) {
      params.push(`q=${encodeURIComponent(filters.search)}`);
    }
    if (filters.filterApp && filters.filterApp.id) {
      params.push(`appId=${encodeURIComponent(filters.filterApp.id)}`);
    }
    if (filters.platforms.length > 0) {
      const platforms = [];
      filters.platforms.forEach((p) => platforms.push(p.id));
      params.push(`platforms=${platforms.join(',')}`);
    }
    if (filters.brd !== null && filters.brd !== '') {
      params.push(`brd=${filters.brd.id}`);
    }
    if (filters.brdsdkversion !== null && filters.brdsdkversion !== '') {
      params.push(`brdsdkversion=${filters.brdsdkversion.id}`);
    }

    if (filters.sessionStartDate) {
      const sessionStartDate = formatDateToUtc(filters.sessionStartDate);
      params.push(`sessionStartDate=${encodeURIComponent(sessionStartDate)}`);
    }
    if (filters.sessionEndDate) {
      const sessionEndDate = formatDateToUtc(filters.sessionEndDate);
      params.push(`sessionEndDate=${encodeURIComponent(sessionEndDate)}`);
    }

    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    getDataList(paramsQuery)
      .then((res) => {
        setOptions({
          ...options,
          loading: false,
          totalRows: res?.data?.totalRows || 0,
          rows: res?.data?.rows || [],
          error: false,
        });
      })
      .catch(() => {
        setOptions({
          ...options,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          error: true,
        });
      });
  }, [options.reloadCounter]);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: 1, py: 2 }}
      >
        <div>&nbsp;</div>
        <div>
          <Button
            color={options.filtersCounter > 0 ? 'warning' : 'inherit'}
            variant="contained"
            startIcon={<Iconify icon="ic:round-filter-list" />}
            sx={{ marginRight: 2 }}
            onClick={handleToggleFilters}
          >
            Filters
            {options.filtersCounter > 0 ? ` (${options.filtersCounter})` : ''}
          </Button>
          <Button
            color="inherit"
            variant="contained"
            startIcon={<Iconify icon="ic:twotone-refresh" />}
            onClick={handleRefreshData}
            disabled={options.loading}
          >
            Refresh
          </Button>
        </div>
      </Stack>

      <Card>
        <ListData
          columns={columns}
          rows={options.rows}
          page={options.page}
          rowsPerPage={options.rowsPerPage}
          totalRows={options.totalRows}
          loading={options.loading}
          actions={actions}
          error={options.error}
          sortBy={options.sortBy}
          sortOrder={options.sortOrder}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          handleClick={handleClick}
          onSortChange={handleSortingChange}
          onAction={handleActionSelect}
        />
      </Card>

      <Drawer
        anchor="right"
        open={openFilters}
        onClose={handleToggleFilters}
        PaperProps={{
          sx: { width: 300, border: 'none', overflow: 'hidden' },
        }}
      >
        <FilterSessions
          values={options.appliedFilters}
          onCancel={handleToggleFilters}
          onSuccess={handleApplyFilters}
          onClear={handleClearFilters}
        />
      </Drawer>

      {doAction.action === ACTIONS.VIEW.value && (
        <ViewSession
          title={`App Session Details - ${doAction.data.deviceId}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
          isLive={doAction.data.isLive}
        />
      )}

      {doAction.action === ACTIONS.GAME_VIDEO_SESSION.value && (
        <GameVideoSession
          title={getDialogTitle(doAction.data)}
          dataId={doAction.data.id}
          appType={doAction.data.appType}
          onCancel={handleActionCancel}
          gameVideoSessions={doAction.data?.gameVideoSessions || 0}
          isLive={doAction.data.isLive}
        />
      )}
      {doAction.action === ACTIONS.RELATED_APP_ITEM_CLICKED.value && (
        <RelatedAppItems
          title={`Related App items Details - ${doAction.data.deviceId}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
          isLive={doAction.data.isLive}
        />
      )}
    </>
  );
};

export default ListSessions;
